/**
 * ── THE REFCOUNT LEDGER (plan §4.2, §4.4; ADR-0003 §4) ──────────────────────────────────────────
 *
 * The runtime plane's truth: which SOURCES are resident, at what refcount, and since when. A
 * source is a parsed GLB / a `Texture.source`, keyed by `(id, hash)` — never by the three object,
 * which is a clone (§4.2). The service owns the objects and the disposal walk; this module owns
 * the ARITHMETIC of when disposal is due, and it is pure so that "acquire twice, release once,
 * not disposable; release again, disposable only after the grace period; re-acquire inside it,
 * disposal cancelled" is a golden under a fake clock rather than a claim about a body swap in a
 * browser.
 *
 * The grace period (default 5 s, plan §11 q2) is what stops a quick re-acquire from thrashing GPU
 * uploads. It is INJECTED at construction and every entry point takes `nowMs`; nothing here reads
 * a clock.
 *
 * ── SHAPE: ONE PLAIN OBJECT, MUTATED IN PLACE ────────────────────────────────────────────────────
 * `LedgerState` is `Record`s and numbers — JSON-serializable, so the dev hook can publish it and
 * a test can `structuredClone` a snapshot. Functions MUTATE the state they are handed and return
 * the fact the caller needs (the new count, the removed entries). Mutation rather than fresh
 * copies because the service calls `acquire`/`release` per handle and the state is the whole
 * resident set; determinism is by construction (no clock, no randomness, ordered walks).
 *
 * ── LANE ────────────────────────────────────────────────────────────────────────────────────────
 * An entry remembers the MOST URGENT lane that acquired it since its count last rose from zero
 * (boot > gameplay > prefetch). That is what `evictionOrder` reads: §4.4 says the `boot` and
 * `gameplay` lanes are never refused — they evict `prefetch` residents first, oldest-released
 * first — so a source held by a boot handle is never an eviction candidate, and one that was
 * prefetched and then adopted by gameplay stops being one the moment gameplay touches it.
 */

import { ASSET_LANES, laneOutranks, type AssetLane } from './types';

export const DEFAULT_GRACE_MS = 5000;

/** `<id>@<hash>` — the one spelling of the ledger key (it appears in dev-hook output and logs). */
export type LedgerKey = string;

export function ledgerKey(id: string, hash: string): LedgerKey {
  return `${id}@${hash}`;
}

export interface LedgerRef {
  id: string;
  hash: string;
}

export interface LedgerEntry {
  key: LedgerKey;
  id: string;
  hash: string;
  /** Live handles. `0` means resident-but-idle: disposable after the grace period. */
  count: number;
  /** A pinned entry never expires and is never an eviction candidate (the loadout's body). */
  pinned: boolean;
  /** The most urgent lane holding it since the count last rose from zero. */
  lane: AssetLane;
  /** When the count last rose from zero. */
  acquiredAtMs: number;
  /** When the count last hit zero; `null` while held. The grace period runs from here. */
  releasedAtMs: number | null;
  /** The accountant's figure for this source — the estimate at `fetching`, corrected at `ready`. */
  vramMB: number;
}

export interface LedgerState {
  graceMs: number;
  entries: Record<LedgerKey, LedgerEntry>;
}

export function createLedger(opts: { graceMs?: number } = {}): LedgerState {
  return { graceMs: opts.graceMs ?? DEFAULT_GRACE_MS, entries: {} };
}

export function getEntry(state: LedgerState, ref: LedgerRef): LedgerEntry | null {
  return state.entries[ledgerKey(ref.id, ref.hash)] ?? null;
}

export interface AcquireOpts {
  lane?: AssetLane;
  /** Charged on FIRST acquire; later calls that pass a figure overwrite it (the `ready` correction). */
  vramMB?: number;
}

/**
 * Take a reference. A first acquire creates the entry; an acquire on an idle entry (count 0,
 * inside or outside the grace period) CANCELS its pending disposal by clearing `releasedAtMs`.
 * Returns the entry after the change.
 */
export function acquire(state: LedgerState, ref: LedgerRef, nowMs: number, opts: AcquireOpts = {}): LedgerEntry {
  const lane = opts.lane ?? 'gameplay';
  const key = ledgerKey(ref.id, ref.hash);
  let e = state.entries[key];
  if (e === undefined) {
    e = {
      key,
      id: ref.id,
      hash: ref.hash,
      count: 0,
      pinned: false,
      lane,
      acquiredAtMs: nowMs,
      releasedAtMs: null,
      vramMB: opts.vramMB ?? 0,
    };
    state.entries[key] = e;
  }
  if (e.count === 0) {
    e.acquiredAtMs = nowMs;
    e.releasedAtMs = null;
    e.lane = lane;
  } else if (laneOutranks(lane, e.lane)) {
    e.lane = lane;
  }
  if (opts.vramMB !== undefined) e.vramMB = opts.vramMB;
  e.count += 1;
  return e;
}

export interface ReleaseResult {
  ok: boolean;
  /** `not-held` when the key is unknown or already at zero — an over-release is a state, not a throw. */
  reason?: 'not-held';
  count: number;
  /** When the entry becomes disposable, or `null` while it is still held or pinned. */
  disposableAtMs: number | null;
}

/** Drop a reference. Idempotent past zero (the second `release()` on one handle is a no-op). */
export function release(state: LedgerState, ref: LedgerRef, nowMs: number): ReleaseResult {
  const e = state.entries[ledgerKey(ref.id, ref.hash)];
  if (e === undefined || e.count === 0) {
    return { ok: false, reason: 'not-held', count: 0, disposableAtMs: null };
  }
  e.count -= 1;
  if (e.count === 0) {
    e.releasedAtMs = nowMs;
    return { ok: true, count: 0, disposableAtMs: e.pinned ? null : nowMs + state.graceMs };
  }
  return { ok: true, count: e.count, disposableAtMs: null };
}

/** Pin: never expires, never evicted, whatever the count. Unknown key → `false`, nothing created. */
export function pin(state: LedgerState, ref: LedgerRef): boolean {
  const e = state.entries[ledgerKey(ref.id, ref.hash)];
  if (e === undefined) return false;
  e.pinned = true;
  return true;
}

/** Unpin. An idle entry's grace period starts NOW (not from the release it had while pinned). */
export function unpin(state: LedgerState, ref: LedgerRef, nowMs: number): boolean {
  const e = state.entries[ledgerKey(ref.id, ref.hash)];
  if (e === undefined) return false;
  e.pinned = false;
  if (e.count === 0) e.releasedAtMs = nowMs;
  return true;
}

/** Overwrite the charged figure (the `ready` correction, §4.4). Unknown key → `false`. */
export function setVram(state: LedgerState, ref: LedgerRef, vramMB: number): boolean {
  const e = state.entries[ledgerKey(ref.id, ref.hash)];
  if (e === undefined) return false;
  e.vramMB = vramMB;
  return true;
}

/** `true` when the entry is idle, unpinned, and its grace period has elapsed at `nowMs`. */
export function isDisposable(state: LedgerState, e: LedgerEntry, nowMs: number): boolean {
  return e.count === 0 && !e.pinned && e.releasedAtMs !== null && nowMs - e.releasedAtMs >= state.graceMs;
}

/**
 * REMOVE and return every entry whose count hit zero at least `graceMs` ago (oldest-released
 * first, key order on ties). The caller disposes the sources it names; a later acquire of the
 * same key starts a fresh entry. Pinned entries never appear.
 */
export function expire(state: LedgerState, nowMs: number): LedgerEntry[] {
  const out = Object.values(state.entries).filter((e) => isDisposable(state, e, nowMs));
  out.sort(byReleasedThenKey);
  for (const e of out) delete state.entries[e.key];
  return out;
}

/** Forcibly drop an entry (an eviction the service has carried out). Returns the entry, or `null`. */
export function evict(state: LedgerState, ref: LedgerRef): LedgerEntry | null {
  const key = ledgerKey(ref.id, ref.hash);
  const e = state.entries[key];
  if (e === undefined) return null;
  delete state.entries[key];
  return e;
}

export interface EvictionFilter {
  /** Restrict candidates to entries whose lane is one of these. Default: every lane. */
  lanes?: readonly AssetLane[];
}

/**
 * Who goes first when the budget is short (§4.4): unpinned IDLE entries oldest-released-first,
 * then sources held ONLY by `prefetch` handles oldest-acquired-first. Never a held `boot` or
 * `gameplay` source — those lanes are never refused, and evicting under a live gameplay handle is
 * a magenta placeholder appearing mid-play. Pure: does not remove anything.
 */
export function evictionOrder(state: LedgerState, filter: EvictionFilter = {}): LedgerEntry[] {
  const lanes = new Set<AssetLane>(filter.lanes ?? ASSET_LANES);
  const idle: LedgerEntry[] = [];
  const heldPrefetch: LedgerEntry[] = [];
  for (const e of Object.values(state.entries)) {
    if (e.pinned || !lanes.has(e.lane)) continue;
    if (e.count === 0) idle.push(e);
    else if (e.lane === 'prefetch') heldPrefetch.push(e);
  }
  idle.sort(byReleasedThenKey);
  heldPrefetch.sort((a, b) => a.acquiredAtMs - b.acquiredAtMs || cmp(a.key, b.key));
  return [...idle, ...heldPrefetch];
}

export interface LedgerTotals {
  /** Every entry — held or idle. What the perf bar's "resident" column shows. */
  resident: number;
  held: number;
  idle: number;
  pinned: number;
  vramMB: number;
  byLane: Record<AssetLane, number>;
}

/** The perf-bar row (§4.4): one walk, no allocation beyond the record. */
export function ledgerTotals(state: LedgerState): LedgerTotals {
  const t: LedgerTotals = {
    resident: 0,
    held: 0,
    idle: 0,
    pinned: 0,
    vramMB: 0,
    byLane: { boot: 0, gameplay: 0, prefetch: 0 },
  };
  for (const e of Object.values(state.entries)) {
    t.resident += 1;
    if (e.count > 0) t.held += 1;
    else t.idle += 1;
    if (e.pinned) t.pinned += 1;
    t.vramMB += e.vramMB;
    t.byLane[e.lane] += 1;
  }
  return t;
}

function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function byReleasedThenKey(a: LedgerEntry, b: LedgerEntry): number {
  return (a.releasedAtMs ?? 0) - (b.releasedAtMs ?? 0) || cmp(a.key, b.key);
}
